import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import generatePseudoRandomNumber from '@app/helper/generate-pseudo-random-number';
import { validationIndentifiers } from '@component/form';
import Icon from '@component/icon';
import Tooltip from '@component/tooltip';

export default class TextArea extends React.Component {
  state = {
    focused: false,
    value: this.props.defaultValue || this.props.value,
  };

  static propTypes = {
    className: PropTypes.string,
    textareaElementClassName: PropTypes.string,
    content: PropTypes.any,
    defaultValue: PropTypes.string,
    description: PropTypes.string,
    label: PropTypes.string,
    maxLength: PropTypes.number,
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    placeholder: PropTypes.string,
    tabIndex: PropTypes.number,
    type: PropTypes.string,
    valid: PropTypes.oneOf([
      validationIndentifiers.NOT_VALID,
      validationIndentifiers.DONT_CHECK,
      validationIndentifiers.VALID,
    ]),
    value: PropTypes.string,
    variant: PropTypes.string,
    resizeOnUpdate: PropTypes.bool,
    hideLabelOnInput: PropTypes.bool,
    textareaRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  };

  static defaultProps = {
    label: '',
    type: 'text',
    valid: validationIndentifiers.DONT_CHECK,
    variant: 'default',
    resizeOnUpdate: true,
    hideLabelOnInput: false,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.handleBlur = this.handleBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.textareaRef = props.textareaRef || React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ value: this.props.value });
    }

    this.resize(this.textareaRef.current);
    this.resize(this.textareaContainer);
  }

  handleFocus(e) {
    this.setState({ focused: true });
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  }

  handleBlur() {
    this.setState({ focused: false });
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  }

  handleChange(e) {
    this.setState({ value: e.target.value });
    if (this.props.onChange) {
      this.props.onChange(e);
    }
  }

  resize(element) {
    if (!this.props.resizeOnUpdate) {
      return;
    }
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }

  render() {
    const {
      label,
      name,
      defaultValue,
      value,
      type,
      onKeyDown,
      valid,
      variant,
      placeholder,
      description,
      className,
      maxLength,
      content,
      tabIndex,
      hideLabelOnInput,
      textareaElementClassName,
    } = this.props;
    const labelClassname = ['textarea__label'];
    const elemClassname = ['textarea', `textarea--${variant}`, className];
    const hasValue = this.state.value || value;

    if (valid === validationIndentifiers.NOT_VALID) {
      elemClassname.push('textarea--invalid');
    }
    if (!label) {
      elemClassname.push('textarea--nolabel');
    }
    if (this.state.focused || (hasValue && !hideLabelOnInput)) {
      labelClassname.push('textarea__label--focused');
      elemClassname.push('textarea--focused');
    }

    if (hasValue && !this.state.focused && hideLabelOnInput) {
      labelClassname.push('textarea__label--hidden');
      elemClassname.push('textarea--nolabel');
    }

    let iconElement = null;
    if (
      valid === validationIndentifiers.VALID &&
      this.state.value &&
      /\S/.test(this.state.value)
    ) {
      iconElement = (
        <div className="textarea__valid">
          <Icon type="Valid" />
        </div>
      );
    } else if (description) {
      iconElement = (
        <div className="textarea__info">
          <Tooltip
            trigger="hover"
            anchor="left"
            offset={20}
            content={<span>{description}</span>}
          >
            <Icon type="Info" />
          </Tooltip>
        </div>
      );
    }

    const pseudoRandomId = `id_${generatePseudoRandomNumber()}`;

    const textareaFieldClassName = classnames(
      'textarea__field',
      textareaElementClassName
    );

    return (
      <div
        className={elemClassname.join(' ')}
        ref={(node) => (this.textareaContainer = node)}
      >
        {label ? (
          <label
            id={pseudoRandomId}
            htmlFor={name}
            className={labelClassname.join(' ')}
          >
            {label}
          </label>
        ) : null}
        <textarea
          aria-labelledby={pseudoRandomId}
          ref={this.textareaRef}
          name={name}
          className={textareaFieldClassName}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          defaultValue={defaultValue}
          type={type}
          onKeyDown={onKeyDown}
          value={value}
          readOnly={variant === 'readonly'}
          placeholder={placeholder}
          maxLength={maxLength}
          tabIndex={tabIndex}
        />
        {iconElement}
        {content}
      </div>
    );
  }
}
